import type { AuthState } from '../auth/AuthContext.js';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class TenantNotApprovedError extends ApiError {
  constructor() {
    super(403, 'tenant_not_approved', 'Your school has not been approved yet.');
    this.name = 'TenantNotApprovedError';
  }
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body: unknown): Promise<T>;
  patch<T>(path: string, body: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
}

export interface CreateApiClientOptions {
  /** Resolves the bearer token. Returns null when unauthenticated. */
  getToken: AuthState['getToken'];
  /** Override for tests. Defaults to relative `/api/...`. */
  baseUrl?: string;
  /** Override fetch — useful in tests. */
  fetchImpl?: typeof fetch;
}

export function createApiClient(options: CreateApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl ?? '';
  const doFetch = options.fetchImpl ?? fetch;

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await options.getToken();
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;

    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    const res = await doFetch(`${baseUrl}${path}`, init);

    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      const code = typeof payload.code === 'string' ? payload.code : 'request_failed';
      const message = typeof payload.message === 'string' ? payload.message : res.statusText;
      if (res.status === 403 && code === 'tenant_not_approved') {
        throw new TenantNotApprovedError();
      }
      throw new ApiError(res.status, code, message);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    put: (path, body) => request('PUT', path, body),
    patch: (path, body) => request('PATCH', path, body),
    delete: (path) => request('DELETE', path),
  };
}
